import React from "react";
import { toast } from "react-toastify";
import { STUN_SERVERS } from "../../utils";

export const useCreatePeerConnection = (
  pcRef,
  wsRef,
  handleLogs,
  remoteRef,
  setRemoteVideoActive,
  localRef,
) => {
  /* Initializes the RTCPeerConnection object. Sets up STUN servers, ICE candidate handlers, and track handlers for remote streams. */
  const createPeerConnection = React.useCallback(
    async (remoteId, remoteName, remoteRole) => {
      try {
        toast.info(
          `Creating peer connection with ${remoteName} (${remoteRole})...`,
          { autoClose: 2000, toastId: `create_pc_${remoteId}` },
        );
        if (pcRef.current) return pcRef.current;
        handleLogs(`Creating RTCPeerConnection for ${remoteId}`);
        pcRef.current = new RTCPeerConnection(STUN_SERVERS);
        if (!remoteId && !remoteName && !remoteRole) {
          handleLogs(
            `Peer connection to ${remoteName} (${remoteRole}) already exists`,
          );

          return pcRef.current;
        }
        // Event handler for when a new ICE candidate is found by the browser
        pcRef.current.onicecandidate = (e) => {
          if (e.candidate && wsRef.current) {
            // Send the found candidate to the other peer via the signaling server
            wsRef.current.send(
              JSON.stringify({
                type: "ice_candidate",
                candidate: e.candidate,
                target_id: remoteId,
              }),
            );
          }
        };

        // Event handler for when a remote media track (video/audio) is received
        pcRef.current.ontrack = (e) => {
          handleLogs("Remote track received: " + e.track.kind);
          // Attach the received stream to the remote video element
          if (remoteRef.current) {
            if (remoteRef.current.srcObject !== e.streams[0]) {
              remoteRef.current.srcObject = e.streams[0];
              setRemoteVideoActive(true);
            }
          }
        };

        // Attach local tracks (camera/mic) to the peer connection if they are already active
        const localStream = localRef.current && localRef.current.srcObject;
        if (localStream) {
          localStream
            .getTracks()
            .forEach((track) => pcRef.current.addTrack(track, localStream));
        }
        return pcRef.current;
      } catch (error) {
        handleLogs("Error creating peer connection:", error);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  return { createPeerConnection };
};
